import { supabaseClient } from "@/lib/supabase";
import { isUuid } from "@/services/isUuid";
import { applyFormTranslations, DEFAULT_LOCALE, type SupportedLocale } from "@patient-watch/i18n";
import type { FormViewT } from "@patient-watch/types";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { fetchFormTranslations } from "./fetchFormTranslations";

export async function selectFormById(formId: string, locale: SupportedLocale = DEFAULT_LOCALE): Promise<FormViewT> {
    if (!isUuid(formId)) {
        throw new Error("Form id is not a uuid");
    }

    const { data, error } = await supabaseClient.from("forms_view").select().eq("form_id", formId).single();

    if (error) throw error;

    const form = data as FormViewT;
    if (locale === DEFAULT_LOCALE) {
        return form;
    }

    const translations = await fetchFormTranslations(form, locale);
    return applyFormTranslations(form, locale, translations) as FormViewT;
}

export const selectFormByIdQueryOptions = (formId: string, locale: SupportedLocale = DEFAULT_LOCALE) =>
    queryOptions({
        queryKey: ["form", { form_id: formId, locale }],
        queryFn: async () => selectFormById(formId, locale),
        enabled: isUuid(formId),
    });

export function useSelectFormById(formId: string, locale: SupportedLocale = DEFAULT_LOCALE) {
    const options = selectFormByIdQueryOptions(formId, locale);
    return useQuery(options);
}
